

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api/baseQuery";

// User Status Enums
export const UserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
};

// Role Types
export const Role = {
  USER: "user",
  TENANT_ADMIN: "tenantAdmin",
};

// Utility Functions
export const userUtils = {
  /**
   * Format phone number for display
   */
  formatPhone: (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("0")) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
    }
    return phone;
  },

  /**
   * Get user status label in Turkish
   */
  getStatusLabel: (status) => {
    const labels = {
      [UserStatus.ACTIVE]: "Aktif",
      [UserStatus.INACTIVE]: "Pasif",
    };
    return labels[status] || status;
  },

  /**
   * Get user status badge color
   */
  getStatusBadgeColor: (status) => {
    const colors = {
      [UserStatus.ACTIVE]: "emerald",
      [UserStatus.INACTIVE]: "gray",
    };
    return colors[status] || "gray";
  },

  /**
   * Basic user data validation
   */
  validateUserData: (data) => {
    const errors = {};

    // Required fields validation
    if (!data.firstName?.trim()) errors.firstName = "Ad gereklidir";
    if (!data.lastName?.trim()) errors.lastName = "Soyad zorunludur";
    if (!data.phoneNumber?.trim()) errors.phoneNumber = "Telefon numarası gereklidir";

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },
};

// API Service Definition
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["User", "UserPermissions"],
  // Enhanced configuration for better synchronization
  refetchOnFocus: true,
  refetchOnReconnect: true,
  keepUnusedDataFor: 60, // 1 minute cache
  endpoints: (builder) => ({
    // Get users list
    getUsers: builder.query({
      query: (params = {}) => {
        const { page = 0, pageSize = 8, keyword = '', status = '', departmentId = '' } = params;
        let url = `/user/list?page=${page}&pageSize=${pageSize}`;
        
        if (keyword.trim()) {
          url += `&keyword=${encodeURIComponent(keyword.trim())}`;
        }
        if (status.trim()) {
          url += `&status=${encodeURIComponent(status)}`;
        }
        if (departmentId.trim()) {
          url += `&departmentId=${encodeURIComponent(departmentId)}`;
        }
        // Only include sort parameters if they differ from server defaults
        if (params.sortBy) {
          url += `&sortBy=${encodeURIComponent(params.sortBy)}`;
        }
        if (params.sortOrder) {
          url += `&sortOrder=${encodeURIComponent(params.sortOrder)}`;
        }
        
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["User"],
      transformResponse: (response) => {
        console.log('🔍 getUsers API Response:', response);
        
        // Handle successful response (even if empty)
        if (response?.isSuccess) {
          return {
            users: response.value?.users || [],
            totalCount: response.value?.totalCount || 0,
            errors: response.errors || [],
            successMessage: response.successMessage
          };
        }
        
        // Handle error response
        if (response?.isSuccess === false) {
          throw new Error(response.errors?.[0] || "Veri alınırken bir hata oluştu");
        }
        
        // Fallback for unexpected response format
        return {
          users: [],
          totalCount: 0,
          errors: [],
          successMessage: null
        };
      },
      transformErrorResponse: (response) => {
        return {
          message: response.data?.errors?.[0] || "Bir hata oluştu",
          errors: response.data?.errors || []
        };
      },
    }),

    // Get current user's profile
    getMyProfile: builder.query({
      query: () => ({
        url: '/user-profile/my-profile',
        method: 'GET',
      }),
      providesTags: ['User', 'MyProfile'],
      transformResponse: (response) => {
        if (response?.isSuccess && response?.value) {
          const v = response.value;
          return {
            userId: v.userId || '',
            birthDate: v.birthDate || null,
            name: v.name || '',
            surname: v.surname || '',
            gender: typeof v.gender === 'number' ? v.gender : 0,
            isKvkkApproved: Boolean(v.isKvkkApproved),
          };
        }
        throw new Error(response?.errors?.[0] || 'Profil bilgileri alınamadı');
      },
      transformErrorResponse: (response) => ({
        message: response.data?.errors?.[0] || 'Profil bilgileri alınamadı',
        errors: response.data?.errors || [],
      }),
    }),

    // Update current user's profile (PUT /user-profile/my-profile)
    // Returns 204 No Content on success
    updateMyProfile: builder.mutation({
      query: (data) => ({
        url: '/user-profile/my-profile',
        method: 'PUT',
        body: {
          name: data.name,
          surname: data.surname,
          birthDate: data.birthDate, // DateOnly format: YYYY-MM-DD
          gender: data.gender, // 0 = Erkek, 1 = Kadın
          ...(data.isKvkkApproved !== undefined && { isKvkkApproved: data.isKvkkApproved }),
        },
      }),
      invalidatesTags: ['User', 'MyProfile'],
      transformResponse: (response, meta) => {
        // Handle 204 No Content - success with empty body
        if (meta?.response?.status === 204 || response === null || response === undefined) {
          return {
            isSuccess: true,
            message: 'Profil başarıyla güncellendi',
          };
        }
        // Handle standard success response
        if (response?.isSuccess) {
          return {
            ...response,
            message: response.successMessage || 'Profil başarıyla güncellendi',
          };
        }
        // If we get here with a response, treat as success (204 case fallback)
        return {
          isSuccess: true,
          message: 'Profil başarıyla güncellendi',
        };
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Profil güncellenirken bir hata oluştu',
        errors: response.data?.errors || [],
      }),
    }),

    // Get user by ID - Enhanced with userName, email, permissions
    getUserById: builder.query({
      query: (id) => ({
        url: `/user/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "User", id }],
      transformResponse: (response) => {
        if (response.isSuccess && response.value) {
          // Enhanced response structure includes userName, email, permissions
          const user = response.value;
          const resolvedFirstName = user.firstName || user.name || '';
          const resolvedLastName = user.lastName || user.surname || '';
          
          return {
            ...user,
            // Ensure permissions is always an array
            permissions: Array.isArray(user.permissions) ? user.permissions : [],
            // Format dates properly
            createdDate: user.createdDate ? new Date(user.createdDate).toISOString() : null,
            // Ensure required fields have fallbacks
            userName: user.userName || '',
            email: user.email || '',
            firstName: resolvedFirstName,
            lastName: resolvedLastName,
            name: user.name || resolvedFirstName,
            surname: user.surname || resolvedLastName,
            phoneNumber: user.phoneNumber || '', // Handle null phoneNumber from API
            role: user.role || 'User'
          };
        }
        throw new Error(response.errors?.[0] || "Çalışan bilgileri alınırken bir hata oluştu");
      },
      transformErrorResponse: (response) => {
        return {
          message: response.data?.errors?.[0] || "Çalışan bulunamadı",
          errors: response.data?.errors || []
        };
      },
    }),

    // Create new user
    createUser: builder.mutation({
      query: (userData) => {
        const payload = {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
          logo: userData.logo || "",
          role: userData.role || Role.USER,
        };

        return {
          url: "/user/create",
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["User"],
      transformResponse: (response) => {
        if (response.isSuccess) {
          return {
            data: response.value,
            successMessage: response.successMessage || "Çalışan başarıyla eklendi",
            errors: response.errors || []
          };
        }
        return {
          errors: response.errors || ["Çalışan eklenirken bir hata oluştu"]
        };
      },
      transformErrorResponse: (response) => {
        return {
          message: response.data?.errors?.[0] || "Çalışan eklenirken bir hata oluştu",
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
    }),

    // Update user information - Enhanced with userName, email
    updateUser: builder.mutation({
      query: (data) => ({
        url: '/user/update',
        method: 'POST', // Using POST as specified by user requirements
        body: {
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          role: data.role,
          permissions: Array.isArray(data.permissions) ? data.permissions : []
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        "User"
      ],
      transformResponse: (response) => {
        if (response?.isSuccess) {
          return {
            ...response,
            message: response.successMessage || 'Çalışan başarıyla güncellendi'
          };
        }
        throw new Error(response?.errors?.[0] || 'Çalışan güncellenirken bir hata oluştu');
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Çalışan güncellenirken bir hata oluştu',
        errors: response.data?.errors || [],
        validationErrors: response.data?.validationErrors || []
      })
    }),

    // Update user permissions (TenantAdmin only)
    updateUserPermissions: builder.mutation({
      query: ({ userId, permissions }) => ({
        url: `/tenant-admin/user/${userId}/permissions`,
        method: 'POST',
        body: {
          permissions: Array.isArray(permissions) ? permissions : []
        }
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "UserPermissions", id: userId },
        "User",
        "UserPermissions"
      ],
      transformResponse: (response) => {
        // Handle different response formats
        console.log('Permission update response:', response);
        
        // If response has isSuccess field and it's true
        if (response?.isSuccess === true) {
          return {
            ...response,
            message: response.successMessage || 'Yetkiler başarıyla güncellendi'
          };
        }
        
        // If response has isSuccess field and it's false
        if (response?.isSuccess === false) {
          throw new Error(response?.errors?.[0] || response?.message || 'Yetkiler güncellenirken bir hata oluştu');
        }
        
        // If no isSuccess field but status is success (200), assume success
        if (!response?.hasOwnProperty('isSuccess')) {
          return {
            isSuccess: true,
            message: 'Yetkiler başarıyla güncellendi',
            data: response
          };
        }
        
        // Default success case
        return {
          ...response,
          message: response.successMessage || 'Yetkiler başarıyla güncellendi'
        };
      },
      transformErrorResponse: (response) => {
        console.log('Permission update error response:', response);
        
        // Handle different error response formats
        const errorData = response.data || response;
        
        return {
          status: response.status || 500,
          message: errorData?.errors?.[0] || errorData?.message || 'Yetkiler güncellenirken bir hata oluştu',
          errors: errorData?.errors || [],
          validationErrors: errorData?.validationErrors || []
        };
      }
    }),

    // Update user by ID using PUT /user/{userId} endpoint
    updateUserById: builder.mutation({
      query: ({ userId, ...data }) => ({
        url: `/user/${userId}`,
        method: 'PUT',
        body: {
          // Backend contract for PUT /user/{userId}
          // Send only editable fields to avoid unintended side effects.
          name: data.name || data.firstName,
          surname: data.surname || data.lastName,
          phoneNumber: data.phoneNumber,
          email: data.email,
        },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
        'User',
        'MyProfile',
      ],
      transformResponse: (response) => {
        if (response?.isSuccess) {
          return {
            ...response,
            message: response.successMessage || 'Kullanıcı başarıyla güncellendi',
          };
        }
        throw new Error(response?.errors?.[0] || 'Kullanıcı güncellenirken bir hata oluştu');
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Kullanıcı güncellenirken bir hata oluştu',
        errors: response.data?.errors || [],
        validationErrors: response.data?.validationErrors || [],
      }),
    }),

    // Update only user details (no permissions change)
    updateUserDetails: builder.mutation({
      query: (data) => ({
        url: '/user/update',
        method: 'POST',
        body: {
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          role: data.role,
          // Do NOT send permissions here to avoid unintended changes
        }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
      ],
      transformResponse: (response) => {
        if (response?.isSuccess) {
          return {
            ...response,
            message: response.successMessage || 'Kullanıcı bilgileri güncellendi'
          };
        }
        throw new Error(response?.errors?.[0] || 'Kullanıcı bilgileri güncellenirken hata oluştu');
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Kullanıcı bilgileri güncellenirken hata oluştu',
        errors: response.data?.errors || [],
        validationErrors: response.data?.validationErrors || []
      })
    }),

    // Delete user by ID using DELETE /user/{userId} endpoint
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
      transformResponse: (response) => {
        if (response?.isSuccess) {
          return {
            ...response,
            message: response.successMessage || 'Kullanıcı başarıyla silindi',
          };
        }
        throw new Error(response?.errors?.[0] || 'Kullanıcı silinirken bir hata oluştu');
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Kullanıcı silinirken bir hata oluştu',
        errors: response.data?.errors || [],
      }),
    }),

    // Upload user profile picture
    uploadUserMedia: builder.mutation({
      query: ({ file, orderNo = 0 }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('orderNo', orderNo.toString());
        
        return {
          url: '/user/media',
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformResponse: (response) => {
        if (response?.isSuccess && response?.value) {
          return {
            url: response.value,
            isSuccess: true,
            message: response.successMessage || 'Resim başarıyla yüklendi'
          };
        }
        throw new Error(response?.errors?.[0] || 'Resim yüklenirken bir hata oluştu');
      },
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response.data?.errors?.[0] || 'Resim yüklenirken bir hata oluştu',
        errors: response.data?.errors || []
      })
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateUserByIdMutation,
  useDeleteUserMutation,
  useUpdateUserPermissionsMutation,
  useUploadUserMediaMutation,
  useUpdateUserDetailsMutation,
  useGetMyProfileQuery,
  useUpdateMyProfileMutation,
} = userApi;

// Export reducer and middleware
export const {
  reducer: userReducer,
  middleware: userMiddleware,
  reducerPath: userReducerPath,
} = userApi;

export default userApi;
